import type { AiProductDraft, BoxListItem } from "../../../api/WarehouseApi";
import { addProductPhoto } from "../../../domain/product-photos";
import { normalizeTypeTags } from "../../../utils/typeTags";
import type { AddProductFormState } from "./form-state";

interface ResolveParentIdFromDraftInput {
  parentId: string | null;
  parentHint: string | null;
  availableBoxes: BoxListItem[];
}

interface ApplyAiProductDraftInput {
  draft: AiProductDraft;
  state: AddProductFormState;
  availableBoxes: BoxListItem[];
  formatParentInputValue: (value: string) => string;
}

function resolveParentIdFromDraft({
  parentId,
  parentHint,
  availableBoxes,
}: ResolveParentIdFromDraftInput): string | null {
  if (parentId && availableBoxes.some((box) => box.id === parentId)) {
    return parentId;
  }

  const hint = (parentHint || "").trim().toLocaleLowerCase("ru-RU");
  if (!hint) {
    return null;
  }

  const match = availableBoxes.find((box) => {
    const name = box.name.trim().toLocaleLowerCase("ru-RU");
    const id = box.id.trim().toLocaleLowerCase("ru-RU");
    return name.includes(hint) || id.includes(hint);
  });

  return match ? match.id : null;
}

export function applyAiProductDraft({
  draft,
  state,
  availableBoxes,
  formatParentInputValue,
}: ApplyAiProductDraftInput): void {
  if (draft.name) {
    state.name = draft.name;
  }

  if (draft.description) {
    state.description = draft.description;
  }

  if (draft.photo) {
    const nextState = addProductPhoto(
      {
        photo: state.photo,
        photos: state.photos,
      },
      draft.photo,
      { makePrimary: true },
    );
    state.photo = nextState.photo;
    state.photos = nextState.photos;
  }

  if (draft.typeTags.length > 0) {
    state.typeTags = normalizeTypeTags([...state.typeTags, ...draft.typeTags]);
  }

  if (draft.measure) {
    state.measure = draft.measure;
  }

  if (draft.quantity) {
    state.quantityInput = String(draft.quantity);
  }

  if (draft.manufacturingDate) {
    state.manufacturingDate = draft.manufacturingDate;
  }

  if (draft.expiryDate) {
    state.expiryDate = draft.expiryDate;
  }

  const parentIdFromDraft = resolveParentIdFromDraft({
    parentId: draft.parentId,
    parentHint: draft.parentHint,
    availableBoxes,
  });

  if (parentIdFromDraft) {
    state.parentId = formatParentInputValue(parentIdFromDraft);
  }
}
